import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  EyeOff, Folder, FolderOpen, FolderTree, Globe, Pencil, Plus, Rocket, Sparkles, Tag, Trash2,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  ActionMenu, ActionMenuDivider, ActionMenuItem, AppBadge, AppButton, AppModal, Checkbox, ConfirmModal,
  EmptyState, FormField, IconButton, Input, NativeSelect, Paginator, SearchInput, Switch, cn,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import GenerateAiQuestionsModal from "./GenerateAiQuestionsModal";
import GenerateAiSubtopicsModal from "./GenerateAiSubtopicsModal";
import { QUESTION_TYPE_LABELS, findFolders, plural, priceLabel } from "./quizBankRules";

const PAGE_SIZE = 24;
// A level with fewer folders than this is read at a glance; a search box would be clutter.
const FIND_FROM = 4;
const PRICE_FORM_ID = "quiz-folder-price-form";

/**
 * QuestionFolders — the question bank as folders: categories, then their
 * subtopics, then the questions inside one. The bank page's default view.
 *
 * Questions are written into a SUBTOPIC (AI drafting and random draws work
 * topic by topic), so a category offers its subtopics and a subtopic offers its
 * questions. In the platform library a folder also carries its marketplace
 * state: published or draft, its price, its pack version.
 *
 * The bank page owns the category dialog and the one toaster: `onAddCategory`,
 * `onAddSubtopic(category)` and `onEditFolder(folder)` open it, `onNotify`
 * shows a message, and bumping `reloadKey` re-reads the tree in place.
 * `onPathChange({ category, subcategory })` tells the page which folder is open,
 * so "Add questions" starts there and its breadcrumbs end there.
 *
 * The open folder is the URL (`?category=…&subcategory=…`, built by
 * quizCenterTrail.folderPath), so a folder can be linked to: the page's
 * breadcrumbs and the question editor's back button return straight to it.
 */
export default function QuestionFolders({
  reloadKey = 0, canUseAi = false, onAddCategory, onAddSubtopic, onEditFolder, onNotify, onPathChange,
}) {
  const navigate = useNavigate();
  const isLibrary = useLocation().pathname.startsWith("/sa/");
  const base = isLibrary ? "/sa/marketplace/quiz-questions" : "/quiz-bank";
  // The page's handlers through refs: a new function on every page render must
  // not re-read the tree or report the path again.
  const handlers = useRef({ onNotify, onPathChange });
  handlers.current = { onNotify, onPathChange };
  const notify = useCallback((message, type = "success") => handlers.current.onNotify?.(message, type), []);

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const catParam = searchParams.get("category") || null;
  const subParam = searchParams.get("subcategory") || null;
  const path = useMemo(() => ({ catId: catParam, subId: catParam && subParam }), [catParam, subParam]);
  const [page, setPage] = useState(1);
  const [questions, setQuestions] = useState({ items: [], total: 0, totalPages: 0 });
  const [qLoading, setQLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [priceFor, setPriceFor] = useState(null);
  const [priceForm, setPriceForm] = useState({ model: "free", period: "", amount: 0 });
  const [savingPrice, setSavingPrice] = useState(false);
  const [deleteFolder, setDeleteFolder] = useState(null);
  const [deleteQ, setDeleteQ] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSubsOpen, setAiSubsOpen] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [find, setFind] = useState("");

  const loadTree = useCallback(async () => {
    const { data } = await apiClient.get("/quiz-categories/library-tree", { headers: { "X-Branch-Id": "all" } });
    setTree(data.tree || []);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadTree()
      .catch(() => { if (alive) notify("The folders could not be loaded.", "error"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [loadTree, notify]);

  const refreshTree = useCallback(async () => {
    try { await loadTree(); } catch { /* keep what is on screen */ }
  }, [loadTree]);

  // A category saved in the page's dialog: re-read quietly, so the open folder
  // does not flash empty.
  const firstReload = useRef(true);
  useEffect(() => {
    if (firstReload.current) { firstReload.current = false; return; }
    refreshTree();
  }, [reloadKey, refreshTree]);

  const currentCat = useMemo(() => tree.find((c) => c._id === path.catId) || null, [tree, path.catId]);
  const currentSub = useMemo(
    () => currentCat?.subfolders?.find((s) => s._id === path.subId) || null,
    [currentCat, path.subId],
  );

  useEffect(() => {
    handlers.current.onPathChange?.({ category: path.catId || "", subcategory: path.subId || "" });
  }, [path.catId, path.subId]);

  const fetchQuestions = useCallback(async () => {
    if (!path.catId) { setQuestions({ items: [], total: 0, totalPages: 0 }); return; }
    setQLoading(true);
    try {
      const params = { category: path.catId, page, limit: PAGE_SIZE, sort: "newest" };
      if (path.subId) params.subcategory = path.subId;
      else params.directOnly = true;
      const { data } = await apiClient.get("/quiz-questions", { params, headers: { "X-Branch-Id": "all" } });
      setQuestions({ items: data.questions || [], total: data.total || 0, totalPages: data.totalPages || 0 });
    } catch {
      setQuestions({ items: [], total: 0, totalPages: 0 });
    }
    setQLoading(false);
  }, [path.catId, path.subId, page]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);
  useEffect(() => { setSel(new Set()); setFind(""); }, [path.catId, path.subId]);

  // ── Navigation ──
  // Opening a folder is a new history entry; leaving one that is gone is not.
  const goTo = useCallback(({ catId = null, subId = null } = {}, { replace = false } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (catId) next.set("category", catId); else next.delete("category");
      if (catId && subId) next.set("subcategory", subId); else next.delete("subcategory");
      return next;
    }, { replace });
    setPage(1);
  }, [setSearchParams]);
  const openCategory = (cat) => goTo({ catId: cat._id });
  const openSub = (sub) => goTo({ catId: path.catId, subId: sub._id });
  const goRoot = () => goTo({}, { replace: true });
  const goCategory = () => goTo({ catId: path.catId }, { replace: true });

  // A link to a folder that is not in the tree — deleted, or another bank's —
  // opens the level above it.
  useEffect(() => {
    if (loading) return;
    if (path.catId && !currentCat) goTo({}, { replace: true });
    else if (path.subId && currentCat && !currentSub) goTo({ catId: path.catId }, { replace: true });
  }, [loading, path.catId, path.subId, currentCat, currentSub, goTo]);
  const openFolder = (folder, kind) => (kind === "category" ? openCategory(folder) : openSub(folder));

  const toggleSel = (id) => setSel((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // ── Folder actions ──
  const run = async (id, work, ok) => {
    setBusy(id);
    try {
      const msg = await work();
      notify(msg || ok);
      await refreshTree();
    } catch (err) {
      notify(err.response?.data?.message || "That did not work. Try again.", "error");
    }
    setBusy(null);
  };

  const togglePublish = (folder) => run(folder._id, async () => {
    await apiClient.put(`/quiz-categories/${folder._id}/publish`, { published: !folder.published });
    return folder.published ? "Hidden from the marketplace" : "Published to the marketplace";
  });

  const releaseVersion = (folder) => run(folder._id, async () => {
    const { data } = await apiClient.put(`/quiz-categories/${folder._id}/release`);
    return data.message;
  }, "New version released");

  const bulkPublish = async (published) => {
    if (!sel.size) return;
    setBulkBusy(true);
    try {
      const { data } = await apiClient.put("/quiz-categories/bulk-publish", { ids: [...sel], published });
      notify(data.message || "Folders updated");
      setSel(new Set());
      await refreshTree();
    } catch (err) {
      notify(err.response?.data?.message || "The folders could not be updated.", "error");
    }
    setBulkBusy(false);
  };

  const handleDeleteFolder = async () => {
    const f = deleteFolder;
    if (!f) return;
    try {
      const { data } = await apiClient.delete(`/quiz-categories/${f._id}?cascade=true`);
      notify(data.message || "Folder deleted");
      if (path.subId === f._id) goCategory();
      else if (path.catId === f._id) goRoot();
      await refreshTree();
    } catch (err) {
      notify(err.response?.data?.message || "The folder could not be deleted.", "error");
    }
    setDeleteFolder(null);
  };

  // ── Pricing ──
  const openPricing = (folder) => {
    const p = folder.pricing || {};
    setPriceForm({ model: p.model || "free", period: p.period || "", amount: p.amount || 0 });
    setPriceFor(folder);
  };

  const savePricing = async (e) => {
    e.preventDefault();
    setSavingPrice(true);
    try {
      await apiClient.put(`/quiz-categories/${priceFor._id}`, {
        marketplace: {
          pricing: {
            model: priceForm.model,
            period: priceForm.model === "paid" ? priceForm.period : "",
            amount: priceForm.model === "paid" ? Number(priceForm.amount) || 0 : 0,
            currency: "INR",
          },
        },
      });
      notify("Price saved");
      setPriceFor(null);
      await refreshTree();
    } catch (err) {
      notify(err.response?.data?.message || "The price could not be saved.", "error");
    }
    setSavingPrice(false);
  };

  // ── Question actions ──
  const toggleQuestionPublic = async (q) => {
    const next = !q.isPublic;
    const flip = (v) => setQuestions((s) => ({ ...s, items: s.items.map((x) => (x._id === q._id ? { ...x, isPublic: v } : x)) }));
    flip(next);
    try {
      await apiClient.put(`/quiz-questions/${q._id}`, { isPublic: next });
      refreshTree();
    } catch (err) {
      flip(!next);
      notify(err.response?.data?.message || "The question could not be updated.", "error");
    }
  };

  const handleDeleteQuestion = async () => {
    const q = deleteQ;
    if (!q) return;
    try {
      await apiClient.delete(`/quiz-questions/${q._id}`);
      setQuestions((s) => ({ ...s, items: s.items.filter((x) => x._id !== q._id), total: Math.max(0, s.total - 1) }));
      notify("Question deleted");
      refreshTree();
    } catch (err) {
      notify(err.response?.data?.message || "The question could not be deleted.", "error");
    }
    setDeleteQ(null);
  };

  const addHere = () => {
    const qs = `category=${path.catId}${path.subId ? `&subcategory=${path.subId}` : ""}`;
    navigate(`${base}/add?${qs}`);
  };

  const insideFolder = !!path.catId;
  const folders = insideFolder ? (path.subId ? [] : (currentCat?.subfolders || [])) : tree;
  const levelName = insideFolder ? "subtopic" : "category";
  const shownFolders = findFolders(folders, find);
  const showQuestions = insideFolder && (qLoading || questions.total > 0);
  const folderIsEmpty = insideFolder && !loading && !qLoading && folders.length === 0 && questions.total === 0;
  const selectMode = isLibrary && sel.size > 0;

  const folderActions = currentSub ? (
    <>
      {canUseAi && (
        <AppButton variant="outline" size="sm" onClick={() => setAiOpen(true)}>
          <Sparkles size={14} aria-hidden="true" /> Draft with AI
        </AppButton>
      )}
      <AppButton size="sm" onClick={addHere}>
        <Plus size={14} aria-hidden="true" /> Add a question here
      </AppButton>
    </>
  ) : (
    <>
      {canUseAi && (
        <AppButton variant="outline" size="sm" onClick={() => setAiSubsOpen(true)}>
          <Sparkles size={14} aria-hidden="true" /> Suggest subtopics with AI
        </AppButton>
      )}
      <AppButton size="sm" onClick={() => onAddSubtopic?.(currentCat)}>
        <Plus size={14} aria-hidden="true" /> Add a subtopic
      </AppButton>
    </>
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <h2 className="tw-m-0 tw-inline-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-content">
          {insideFolder
            ? <FolderOpen size={18} aria-hidden="true" className="tw-shrink-0 tw-text-primary" />
            : <FolderTree size={18} aria-hidden="true" className="tw-shrink-0 tw-text-primary" />}
          <span className="tw-truncate">
            {currentSub?.name
              || (currentCat ? `${currentCat.icon ? `${currentCat.icon} ` : ""}${currentCat.name}` : "All categories")}
          </span>
        </h2>
        {insideFolder && currentCat && <div className="tw-flex tw-flex-wrap tw-gap-2">{folderActions}</div>}
      </div>

      {!insideFolder && tree.length > 0 && (
        <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
          {isLibrary ? (
            <>
              A question reaches institutes only when its folder is <strong>Published</strong> and the question is{" "}
              <strong>Public</strong>. Use ⋮ on a folder to publish it, price it or delete it.
            </>
          ) : (
            "Open a category, then a subtopic, to see and add its questions. Use ⋮ on a folder to edit or delete it."
          )}
        </p>
      )}

      {loading ? (
        <div className="tw-grid tw-gap-3 tw-grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
          {[0, 1, 2, 3].map((i) => <Shimmer key={i} height={112} />)}
        </div>
      ) : tree.length === 0 ? (
        <EmptyState
          icon={FolderTree}
          title="No categories yet"
          description="Make a category for each subject or round — then subtopics inside it, then questions."
          action={(
            <AppButton onClick={() => onAddCategory?.()}>
              <Plus size={16} aria-hidden="true" /> Create a category
            </AppButton>
          )}
        />
      ) : (
        <>
          {folders.length > 0 && (
            <section aria-label={insideFolder ? "Subtopics" : "Categories"} className="tw-flex tw-flex-col tw-gap-2.5">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <h3 className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
                  {insideFolder ? "Subtopics" : "Categories"} · {folders.length}
                </h3>
                {folders.length >= FIND_FROM && (
                  <SearchInput
                    value={find}
                    onChange={setFind}
                    placeholder={`Find a ${levelName}…`}
                    aria-label={`Find a ${levelName}`}
                  />
                )}
              </div>

              {selectMode && (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-primary/40 tw-bg-primary-soft tw-px-3 tw-py-2">
                  <span className="tw-mr-auto tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                    {plural(sel.size, "folder")} selected
                  </span>
                  <AppButton size="sm" disabled={bulkBusy} onClick={() => bulkPublish(true)}>
                    <Globe size={14} aria-hidden="true" /> {bulkBusy ? "Updating…" : "Publish to marketplace"}
                  </AppButton>
                  <AppButton variant="outline" size="sm" disabled={bulkBusy} onClick={() => bulkPublish(false)}>
                    <EyeOff size={14} aria-hidden="true" /> Unpublish
                  </AppButton>
                  <AppButton variant="ghost" size="sm" onClick={() => setSel(new Set())}>Clear</AppButton>
                </div>
              )}

              {shownFolders.length === 0 && (
                <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted" role="status">
                  No {levelName === "category" ? "categories" : "subtopics"} match “{find.trim()}”.
                </p>
              )}

              <ul className="tw-m-0 tw-grid tw-list-none tw-gap-3 tw-p-0 tw-grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
                {shownFolders.map((f) => {
                  const kind = insideFolder ? "subtopic" : "category";
                  return (
                    <FolderTile
                      key={f._id}
                      folder={f}
                      kind={kind}
                      isLibrary={isLibrary}
                      selected={sel.has(f._id)}
                      busy={busy === f._id}
                      onActivate={() => (selectMode ? toggleSel(f._id) : openFolder(f, kind))}
                      onToggleSelect={() => toggleSel(f._id)}
                      menu={(
                        <ActionMenu label={`Actions for ${f.name}`}>
                          <ActionMenuItem onClick={() => openFolder(f, kind)}>
                            <FolderOpen size={14} aria-hidden="true" /> Open
                          </ActionMenuItem>
                          {onEditFolder && (
                            <ActionMenuItem onClick={() => onEditFolder(f)}>
                              <Pencil size={14} aria-hidden="true" /> Edit details
                            </ActionMenuItem>
                          )}
                          {isLibrary && (
                            <>
                              <ActionMenuItem onClick={() => togglePublish(f)} disabled={busy === f._id}>
                                {f.published
                                  ? <><EyeOff size={14} aria-hidden="true" /> Unpublish</>
                                  : <><Globe size={14} aria-hidden="true" /> Publish</>}
                              </ActionMenuItem>
                              <ActionMenuItem onClick={() => openPricing(f)}>
                                <Tag size={14} aria-hidden="true" /> Price
                              </ActionMenuItem>
                              {kind === "category" && f.published && f.pendingRelease && (
                                <ActionMenuItem onClick={() => releaseVersion(f)} disabled={busy === f._id}>
                                  <Rocket size={14} aria-hidden="true" /> Release v{(f.version || 1) + 1}
                                </ActionMenuItem>
                              )}
                            </>
                          )}
                          <ActionMenuDivider />
                          <ActionMenuItem danger onClick={() => setDeleteFolder(f)}>
                            <Trash2 size={14} aria-hidden="true" /> Delete {kind === "category" ? "category" : "subtopic"}
                          </ActionMenuItem>
                        </ActionMenu>
                      )}
                    />
                  );
                })}
              </ul>
            </section>
          )}

          {showQuestions && (
            <section aria-label="Questions" className="tw-flex tw-flex-col tw-gap-2.5">
              <h3 className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
                {path.subId ? "Questions" : "Questions directly in this category"}
                {!qLoading && questions.total > 0 ? ` · ${questions.total}` : ""}
              </h3>
              {qLoading ? (
                <Shimmer height={56} count={4} />
              ) : (
                <ul className="tw-m-0 tw-list-none tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-3 tw-py-0">
                  {questions.items.map((q) => (
                    <QuestionRow
                      key={q._id}
                      q={q}
                      isLibrary={isLibrary}
                      onEdit={() => navigate(`${base}/edit/${q._id}`)}
                      onDelete={() => setDeleteQ(q)}
                      onTogglePublic={() => toggleQuestionPublic(q)}
                    />
                  ))}
                </ul>
              )}
              <Paginator
                page={page}
                totalPages={questions.totalPages}
                total={questions.total}
                pageSize={PAGE_SIZE}
                onChange={setPage}
                infoLabel="questions"
              />
            </section>
          )}

          {folderIsEmpty && (
            <EmptyState
              icon={Folder}
              title={path.subId ? "No questions in this subtopic yet" : "This category has no subtopics yet"}
              description={path.subId
                ? "Add the first one, or let AI draft a few for you to check."
                : "Questions live in subtopics. Add one — Percentages, Joins, Reading comprehension — then its questions."}
              action={<div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">{folderActions}</div>}
            />
          )}
        </>
      )}

      {/* ── Price ── */}
      <AppModal
        show={!!priceFor}
        onHide={() => !savingPrice && setPriceFor(null)}
        title={`Price “${priceFor?.name || ""}”`}
        size="sm"
        footer={(
          <>
            <AppButton variant="outline" type="button" onClick={() => setPriceFor(null)} disabled={savingPrice}>Cancel</AppButton>
            <AppButton type="submit" form={PRICE_FORM_ID} disabled={savingPrice}>{savingPrice ? "Saving…" : "Save price"}</AppButton>
          </>
        )}
      >
        <form id={PRICE_FORM_ID} onSubmit={savePricing} className="tw-flex tw-flex-col tw-gap-4">
          <FormField label="Price">
            <NativeSelect
              value={priceForm.model}
              onChange={(e) => setPriceForm((p) => ({ ...p, model: e.target.value }))}
              options={[{ value: "free", label: "Free" }, { value: "paid", label: "Paid" }]}
            />
          </FormField>
          {priceForm.model === "paid" && (
            <div className="tw-grid tw-gap-3 min-[480px]:tw-grid-cols-2">
              <FormField label="Billing">
                <NativeSelect
                  value={priceForm.period}
                  onChange={(e) => setPriceForm((p) => ({ ...p, period: e.target.value }))}
                  options={[
                    { value: "", label: "One-time" },
                    { value: "monthly", label: "Monthly" },
                    { value: "yearly", label: "Yearly" },
                  ]}
                />
              </FormField>
              <FormField label="Amount (₹)">
                <Input
                  type="number"
                  min="0"
                  value={priceForm.amount}
                  onChange={(e) => setPriceForm((p) => ({ ...p, amount: e.target.value }))}
                />
              </FormField>
            </div>
          )}
          <p className="tw-m-0 tw-text-xs tw-text-content-muted">Subtopics take this price unless they have their own.</p>
        </form>
      </AppModal>

      {aiOpen && currentCat && currentSub && (
        <GenerateAiQuestionsModal
          category={currentCat}
          subcategory={currentSub}
          onClose={() => setAiOpen(false)}
          onSaved={(message) => {
            setAiOpen(false);
            notify(message);
            fetchQuestions();
            refreshTree();
          }}
        />
      )}

      {aiSubsOpen && currentCat && !currentSub && (
        <GenerateAiSubtopicsModal
          category={currentCat}
          onClose={() => setAiSubsOpen(false)}
          onSaved={(message) => {
            setAiSubsOpen(false);
            notify(message);
            refreshTree();
          }}
        />
      )}

      <ConfirmModal
        show={!!deleteFolder}
        onHide={() => setDeleteFolder(null)}
        onConfirm={handleDeleteFolder}
        title={`Delete ${deleteFolder?.subfolders !== undefined ? "category" : "subtopic"}?`}
        message={deleteFolder && <DeleteFolderMessage folder={deleteFolder} />}
        confirmText="Delete"
        variant="danger"
      />
      <ConfirmModal
        show={!!deleteQ}
        onHide={() => setDeleteQ(null)}
        onConfirm={handleDeleteQuestion}
        title="Delete question?"
        message={deleteQ
          ? <>This permanently deletes <strong>“{shorten(deleteQ.questionText)}”</strong>.</>
          : "This permanently deletes the question."}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}

const shorten = (text = "", n = 110) => (text.length > n ? `${text.slice(0, n)}…` : text);

/** A folder: its name opens it (the whole tile is the hit area), a tick selects
 *  it for a bulk publish in the library, ⋮ holds everything else. */
function FolderTile({ folder, kind, isLibrary, selected, busy, onActivate, onToggleSelect, menu }) {
  const isCat = kind === "category";
  const count = (isCat ? folder.totalCount : folder.count) || 0;
  const live = isCat ? folder.totalPublicCount : folder.publicCount;
  return (
    <li
      className={cn(
        "tw-relative tw-flex tw-flex-col tw-gap-2 tw-rounded tw-border tw-border-solid tw-p-3.5 tw-transition-colors",
        "focus-within:tw-shadow-focus hover:tw-border-primary/50",
        selected ? "tw-border-primary tw-bg-primary-soft" : "tw-border-line tw-bg-surface-raised",
        busy && "tw-opacity-70",
      )}
    >
      <div className="tw-flex tw-items-start tw-gap-2">
        {isLibrary && (
          <span className="tw-relative tw-z-10">
            <Checkbox checked={selected} onChange={onToggleSelect} aria-label={`Select ${folder.name}`} />
          </span>
        )}
        <span aria-hidden="true" className="tw-text-[1.4rem] tw-leading-none tw-text-primary">
          {folder.icon || <Folder size={24} />}
        </span>
        <span className="tw-relative tw-z-10 tw-ml-auto">{menu}</span>
      </div>
      <button
        type="button"
        onClick={onActivate}
        title={folder.name}
        className="tw-m-0 tw-cursor-pointer tw-truncate tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-sm tw-font-semibold tw-text-content focus:tw-outline-none after:tw-absolute after:tw-inset-0 after:tw-content-['']"
      >
        {folder.name}
      </button>
      {folder.description && (
        <p className="tw-m-0 tw-line-clamp-2 tw-text-xs tw-text-content-muted">{folder.description}</p>
      )}
      <p className="tw-m-0 tw-text-xs tw-text-content-muted">
        {plural(count, "question")}
        {isCat && ` · ${plural(folder.subfolders?.length || 0, "subtopic")}`}
        {isLibrary && live > 0 && ` · ${live} live`}
      </p>
      {isLibrary && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
          <AppBadge size="sm" {...(folder.published ? { color: "emerald" } : { variant: "secondary" })}>
            {folder.published ? "Published" : "Draft"}
          </AppBadge>
          {isCat && folder.published && <AppBadge size="sm" variant="outline">v{folder.version || 1}</AppBadge>}
          {isCat && folder.pendingRelease && (
            <AppBadge size="sm" color="amber" title="Content changed since the last release — release a new version so institutes see it.">
              Unreleased changes
            </AppBadge>
          )}
          <AppBadge size="sm" {...(folder.pricing?.model === "paid" ? { color: "violet" } : { variant: "outline" })}>
            {priceLabel(folder.pricing)}
          </AppBadge>
        </div>
      )}
    </li>
  );
}

function QuestionRow({ q, isLibrary, onEdit, onDelete, onTogglePublic }) {
  return (
    <li className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-py-2.5 first:tw-border-t-0">
      <span className="tw-min-w-[12rem] tw-flex-1 tw-line-clamp-2 tw-text-[0.8125rem] tw-text-content">{q.questionText}</span>
      <AppBadge size="sm" variant="secondary">{QUESTION_TYPE_LABELS[q.questionType] || q.questionType}</AppBadge>
      {isLibrary && (
        <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-xs tw-text-content-muted">
          <Switch checked={q.isPublic !== false} onChange={onTogglePublic} aria-label="Show in the marketplace" />
          {q.isPublic !== false ? "Public" : "Hidden"}
        </span>
      )}
      <span className="tw-flex tw-items-center tw-gap-1">
        <IconButton icon={Pencil} label="Edit question" size="sm" onClick={onEdit} />
        <IconButton icon={Trash2} label="Delete question" size="sm" variant="ghost-destructive" onClick={onDelete} />
      </span>
    </li>
  );
}

function DeleteFolderMessage({ folder }) {
  const isCat = folder.subfolders !== undefined;
  const subs = folder.subfolders?.length || 0;
  const qs = (isCat ? folder.totalCount : folder.count) || 0;
  return (
    <>
      This permanently deletes <strong>{folder.name}</strong>
      {subs > 0 && <>, its {plural(subs, "subtopic")}</>}
      {qs > 0 && <> and {plural(qs, "question")}</>}. This cannot be undone.
    </>
  );
}
